import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import MonthDayPicker from "@/components/MonthDayPicker";
import { Shield, Cake } from "lucide-react";
import { toast } from "sonner";

export default function Register() {
  const { register, settings } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    role: "player",
    jersey_number: "",
    position: "",
    birthday: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    const payload = {
      name: form.name,
      email: form.email,
      password: form.password,
      role: form.role,
      jersey_number: form.jersey_number ? Number(form.jersey_number) : null,
      position: form.position || null,
      birthday: form.birthday || null,
    };
    const res = await register(payload);
    setLoading(false);
    if (res.ok) {
      toast.success(res.user?.is_pending ? "Compte créé — en attente de validation" : "Compte créé");
      navigate(res.user?.is_pending ? "/pending-approval" : "/dashboard");
    } else {
      setError(res.error);
      toast.error(res.error);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[#0B0F17] tactical-grid">
      <div className="w-full max-w-lg bg-slate-900/70 backdrop-blur-xl border border-white/10 rounded-2xl p-8 slide-up">
        <div className="flex items-center gap-3 mb-6">
          {settings.app_logo_url ? (
            <img
              src={settings.app_logo_url}
              alt="Logo"
              className="w-11 h-11 rounded-full object-cover"
            />
          ) : (
            <div className="w-11 h-11 rounded-full bg-[#E5B85C] flex items-center justify-center">
              <Shield className="w-6 h-6 text-[#0B0F17]" strokeWidth={2.5} />
            </div>
          )}
          <div className="font-display font-black text-2xl uppercase">
            {settings.app_name || "ASS les Régionnaires"}
          </div>
        </div>
        <div className="text-[11px] font-mono-stat uppercase tracking-[0.25em] text-slate-500 mb-2">
          Nouveau compte
        </div>
        <h2 className="font-display font-black text-3xl sm:text-4xl uppercase tracking-tight mb-6">
          Rejoindre le club
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-mono-stat uppercase tracking-widest text-slate-400 mb-2">
              Nom complet
            </label>
            <input
              data-testid="register-name-input"
              type="text"
              required
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              className="w-full px-4 py-3 bg-slate-900 border border-white/10 rounded-lg focus:border-[#E5B85C] focus:outline-none text-white"
              placeholder="Kylian Dupont"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-mono-stat uppercase tracking-widest text-slate-400 mb-2">
                Email
              </label>
              <input
                data-testid="register-email-input"
                type="email"
                required
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
                className="w-full px-4 py-3 bg-slate-900 border border-white/10 rounded-lg focus:border-[#E5B85C] focus:outline-none text-white"
                placeholder="you@club.fc"
              />
            </div>
            <div>
              <label className="block text-xs font-mono-stat uppercase tracking-widest text-slate-400 mb-2">
                Mot de passe
              </label>
              <input
                data-testid="register-password-input"
                type="password"
                required
                minLength={6}
                value={form.password}
                onChange={(e) => set("password", e.target.value)}
                className="w-full px-4 py-3 bg-slate-900 border border-white/10 rounded-lg focus:border-[#E5B85C] focus:outline-none text-white"
                placeholder="6+ caractères"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-mono-stat uppercase tracking-widest text-slate-400 mb-2">
              Rôle
            </label>
            <div className="grid grid-cols-2 gap-3">
              {["player", "coach"].map((r) => (
                <button
                  key={r}
                  type="button"
                  data-testid={`register-role-${r}`}
                  onClick={() => set("role", r)}
                  className={`py-3 rounded-lg text-sm font-bold uppercase tracking-wide transition-colors ${
                    form.role === r
                      ? "bg-[#E5B85C] text-[#0B0F17]"
                      : "bg-slate-800 text-slate-300 hover:bg-slate-700"
                  }`}
                >
                  {r === "player" ? "Joueur" : "Entraîneur"}
                </button>
              ))}
            </div>
          </div>

          {form.role === "player" && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-mono-stat uppercase tracking-widest text-slate-400 mb-2">
                  N° maillot
                </label>
                <input
                  data-testid="register-jersey-input"
                  type="number"
                  min="1"
                  max="99"
                  value={form.jersey_number}
                  onChange={(e) => set("jersey_number", e.target.value)}
                  className="w-full px-4 py-3 bg-slate-900 border border-white/10 rounded-lg focus:border-[#E5B85C] focus:outline-none text-white"
                  placeholder="10"
                />
              </div>
              <div>
                <label className="block text-xs font-mono-stat uppercase tracking-widest text-slate-400 mb-2">
                  Poste
                </label>
                <input
                  data-testid="register-position-input"
                  type="text"
                  value={form.position}
                  onChange={(e) => set("position", e.target.value)}
                  className="w-full px-4 py-3 bg-slate-900 border border-white/10 rounded-lg focus:border-[#E5B85C] focus:outline-none text-white"
                  placeholder="Attaquant"
                />
              </div>
            </div>
          )}

          <div>
            <label className="block text-xs font-mono-stat uppercase tracking-widest text-slate-400 mb-2 flex items-center gap-1">
              <Cake className="w-3 h-3 text-[#E5B85C]" /> Anniversaire — jour et mois (facultatif)
            </label>
            <MonthDayPicker
              testIdPrefix="register-birthday"
              value={form.birthday}
              onChange={(v) => set("birthday", v)}
            />
            <div className="mt-1 text-[11px] text-slate-500">
              Le club te souhaitera un joyeux anniversaire — pas besoin de l'année.
            </div>
          </div>

          {error && (
            <div
              data-testid="register-error"
              className="p-3 rounded-lg bg-rose-500/10 border border-rose-500/30 text-rose-300 text-sm"
            >
              {error}
            </div>
          )}
          <button
            data-testid="register-submit-button"
            type="submit"
            disabled={loading}
            className="w-full py-3.5 bg-[#E5B85C] hover:bg-[#D4A24E] text-[#0B0F17] font-bold uppercase tracking-wide rounded-lg disabled:opacity-50 transition-colors"
          >
            {loading ? "Création..." : "Créer mon compte"}
          </button>
        </form>

        <div className="mt-6 text-center text-sm text-slate-400">
          Déjà membre ?{" "}
          <Link
            data-testid="go-login-link"
            to="/login"
            className="text-[#E5B85C] hover:text-[#D4A24E] font-semibold"
          >
            Se connecter
          </Link>
        </div>
      </div>
    </div>
  );
}
